<template>
  <div class="checkin-page">
    <div class="ci-header">
      <h1>每日签到</h1>
      <p>连续签到得更多金币，坚持解锁精彩漫画</p>
    </div>

    <div v-if="loading" class="ci-loading">加载中…</div>

    <template v-else>
      <!-- 概览卡 -->
      <div class="ci-overview">
        <div class="ov-item">
          <div class="ov-num">{{ status.continuous_days || 0 }}</div>
          <div class="ov-label">连续签到（天）</div>
        </div>
        <div class="ov-divider"></div>
        <div class="ov-item">
          <div class="ov-num">{{ status.total_days || 0 }}</div>
          <div class="ov-label">累计签到（天）</div>
        </div>
        <div class="ov-divider"></div>
        <div class="ov-item">
          <div class="ov-num">{{ status.total_coins || 0 }}</div>
          <div class="ov-label">签到累计金币</div>
        </div>
      </div>

      <!-- 主签到按钮 -->
      <div class="ci-action">
        <button
          class="sign-btn"
          :class="{ signed: status.today_signed }"
          :disabled="status.today_signed || signing"
          @click="handleSign"
        >
          <template v-if="status.today_signed">今日已签到</template>
          <template v-else-if="signing">签到中…</template>
          <template v-else>立即签到</template>
        </button>
        <p class="tip">明日签到可得 <b>{{ nextReward }}</b> 金币</p>
      </div>

      <!-- 连续奖励进度 -->
      <div class="ci-rewards">
        <h2>连续签到奖励</h2>
        <div class="reward-track">
          <div
            v-for="r in rewardRules"
            :key="r.day"
            class="reward-node"
            :class="{ reached: (status.continuous_days || 0) >= r.day }"
          >
            <div class="rn-coin">+{{ r.coins }}</div>
            <div class="rn-day">第{{ r.day }}天</div>
          </div>
        </div>
      </div>

      <!-- 本月签到日历 -->
      <div class="ci-calendar">
        <div class="cal-head">
          <h2>{{ calYear }}年{{ calMonth }}月</h2>
          <span class="cal-sub">本月已签 {{ signedDates.length }} 天</span>
        </div>
        <div class="cal-week">
          <span v-for="w in ['日','一','二','三','四','五','六']" :key="w">{{ w }}</span>
        </div>
        <div class="cal-grid">
          <span v-for="b in firstDayOffset" :key="'b'+b" class="cal-cell blank"></span>
          <span
            v-for="d in daysInMonth"
            :key="d"
            class="cal-cell"
            :class="{ signed: signedDates.includes(d), today: d === todayDate }"
          >
            <span class="cal-d">{{ d }}</span>
            <svg v-if="signedDates.includes(d)" class="cal-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="m5 13 4 4L19 7"/></svg>
          </span>
        </div>
      </div>
    </template>

    <!-- 签到成功弹层 -->
    <div v-if="rewardPop" class="reward-pop" @click="rewardPop = false">
      <div class="pop-card" @click.stop>
        <div class="pop-coin">+{{ lastReward }}</div>
        <p class="pop-title">签到成功</p>
        <p class="pop-sub">已连续签到 {{ status.continuous_days }} 天</p>
        <button class="gr-btn gr-btn-primary" @click="rewardPop = false">开心收下</button>
      </div>
    </div>
  </div>
</template>

<script>
import { getSignStatus, doSignIn } from '../api/index';

export default {
  name: 'CheckInPage',
  data() {
    const now = new Date();
    return {
      loading: true,
      signing: false,
      rewardPop: false,
      lastReward: 0,
      status: {
        today_signed: false,
        continuous_days: 0,
        total_days: 0,
        total_coins: 0,
        signed_dates: []
      },
      rewardRules: [
        { day: 1, coins: 5 },
        { day: 3, coins: 10 },
        { day: 7, coins: 20 },
        { day: 15, coins: 40 },
        { day: 30, coins: 100 }
      ],
      calYear: now.getFullYear(),
      calMonth: now.getMonth() + 1,
      todayDate: now.getDate()
    };
  },
  computed: {
    signedDates() {
      // 后端返回本月已签日期数组（day 数字）或日期字符串，做兼容
      const arr = this.status.signed_dates || [];
      return arr
        .map(x => (typeof x === 'number' ? x : parseInt(String(x).slice(-2), 10)))
        .filter(n => !isNaN(n));
    },
    daysInMonth() {
      return new Date(this.calYear, this.calMonth, 0).getDate();
    },
    firstDayOffset() {
      return new Date(this.calYear, this.calMonth - 1, 1).getDay();
    },
    nextReward() {
      const next = (this.status.continuous_days || 0) + (this.status.today_signed ? 1 : 1);
      let reward = this.rewardRules[0].coins;
      for (const r of this.rewardRules) {
        if (next >= r.day) reward = r.coins;
      }
      return reward;
    }
  },
  mounted() {
    this.loadStatus();
  },
  methods: {
    async loadStatus() {
      this.loading = true;
      try {
        const res = await getSignStatus();
        if (res.data && res.data.code === 1) {
          this.status = { ...this.status, ...res.data.data };
        }
      } catch (e) {
        // 后端未就绪时用本地占位，页面仍可预览
        console.warn('签到状态获取失败，使用占位数据', e);
      } finally {
        this.loading = false;
      }
    },
    async handleSign() {
      if (this.status.today_signed || this.signing) return;
      this.signing = true;
      try {
        const res = await doSignIn();
        if (res.data && res.data.code === 1) {
          const d = res.data.data || {};
          this.lastReward = d.coins || this.nextReward;
          this.status.today_signed = true;
          this.status.continuous_days = d.continuous_days || (this.status.continuous_days + 1);
          this.status.total_days = d.total_days || (this.status.total_days + 1);
          this.status.total_coins = (this.status.total_coins || 0) + this.lastReward;
          if (!this.signedDates.includes(this.todayDate)) {
            this.status.signed_dates = [...(this.status.signed_dates || []), this.todayDate];
          }
          this.rewardPop = true;
        } else {
          alert(res.data?.msg || '签到失败，请稍后重试');
        }
      } catch (e) {
        alert('签到失败：' + (e.message || '网络异常'));
      } finally {
        this.signing = false;
      }
    }
  }
};
</script>

<style scoped>
.checkin-page { max-width: 860px; margin: 0 auto; color: var(--text); }
.ci-header h1 { font-size: 26px; font-weight: 700; }
.ci-header p { color: var(--text-dim); margin-top: 6px; font-size: 14px; }
.ci-loading { padding: 60px 0; text-align: center; color: var(--text-dim); }

.ci-overview {
  display: flex; align-items: center; justify-content: space-around;
  background: var(--surface); border: 1px solid var(--border);
  border-radius: var(--radius-lg); padding: 24px; margin-top: 24px;
}
.ov-item { text-align: center; }
.ov-num { font-size: 30px; font-weight: 800; color: var(--brand); }
.ov-label { font-size: 13px; color: var(--text-dim); margin-top: 4px; }
.ov-divider { width: 1px; height: 40px; background: var(--border); }

.ci-action { text-align: center; margin: 32px 0; }
.sign-btn {
  width: 180px; height: 56px; border: none; cursor: pointer;
  border-radius: var(--radius-pill); font-size: 17px; font-weight: 700; color: #fff;
  background: var(--brand); transition: background var(--t-fast), transform var(--t-fast);
}
.sign-btn:hover:not(:disabled) { background: var(--brand-hover); transform: translateY(-2px); }
.sign-btn.signed, .sign-btn:disabled { background: var(--surface-3); color: var(--text-dim); cursor: default; }
.ci-action .tip { margin-top: 12px; color: var(--text-dim); font-size: 13px; }
.ci-action .tip b { color: var(--gold); }

.ci-rewards, .ci-calendar {
  background: var(--surface); border: 1px solid var(--border);
  border-radius: var(--radius-lg); padding: 24px; margin-top: 20px;
}
.ci-rewards h2, .cal-head h2 { font-size: 16px; font-weight: 700; margin-bottom: 18px; }
.reward-track { display: flex; justify-content: space-between; gap: 8px; }
.reward-node {
  flex: 1; text-align: center; padding: 14px 6px; border-radius: var(--radius);
  background: var(--surface-2); border: 1px solid var(--border); position: relative;
}
.reward-node.reached { background: var(--brand-soft); border-color: transparent; }
.rn-coin { font-size: 16px; font-weight: 800; color: var(--text-dim); }
.reward-node.reached .rn-coin { color: var(--brand); }
.rn-day { font-size: 12px; color: var(--text-faint); margin-top: 4px; }

.cal-head { display: flex; align-items: baseline; justify-content: space-between; }
.cal-sub { font-size: 13px; color: var(--text-dim); }
.cal-week { display: grid; grid-template-columns: repeat(7, 1fr); gap: 6px; margin-bottom: 8px; }
.cal-week span { text-align: center; font-size: 12px; color: var(--text-faint); }
.cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 6px; }
.cal-cell {
  position: relative; aspect-ratio: 1; display: flex; align-items: center; justify-content: center;
  border-radius: 10px; background: var(--surface-2); font-size: 13px; color: var(--text-dim);
}
.cal-cell.blank { background: transparent; }
.cal-cell.signed { background: var(--brand-soft); color: var(--brand); }
.cal-cell.today { border: 1px solid var(--brand); color: var(--text); }
.cal-check { position: absolute; width: 14px; height: 14px; right: 4px; bottom: 4px; color: var(--brand); }

.reward-pop {
  position: fixed; inset: 0; background: rgba(0,0,0,.55);
  display: flex; align-items: center; justify-content: center; z-index: 2000;
}
.pop-card {
  background: var(--surface); border: 1px solid var(--border-strong); border-radius: var(--radius-lg);
  padding: 32px 40px; text-align: center; width: 300px;
}
.pop-coin { font-size: 44px; font-weight: 800; color: var(--gold); }
.pop-title { font-size: 18px; font-weight: 700; margin-top: 8px; }
.pop-sub { color: var(--text-dim); font-size: 13px; margin: 6px 0 20px; }

@media (max-width: 768px) {
  .ci-overview { padding: 18px; }
  .ov-num { font-size: 24px; }
  .reward-node { padding: 10px 3px; }
  .rn-coin { font-size: 14px; }
}
</style>
